/**
 * Tasks, issues and questions (`MCK-20260521-008`, C7/C8) — the first caller of a backend
 * that shipped complete on 2026-08-20 and had none.
 *
 * Nothing here needed a server change except the version signal of `KLQ-20260821-001` §E, and
 * that one shipped in phase 1. A field this screen wants that the API does not have is a spec,
 * not a client-side derivation.
 *
 * THREE THINGS THE CONTRACT SAYS THAT THIS FILE IS BUILT AROUND
 *
 * 1. **Filtering is the server's.** Every narrowing is a query parameter, answered in the
 *    `WHERE`, so a page is complete and `has_more` is honest. A filtered slice of a broader
 *    list cannot fill a page and would be wrong about everything past the first one — the
 *    reviews queue wrote that reasoning down first (`features/reviews/api.ts`).
 * 2. **One filter object serves the list, the board and the export**, because the server
 *    builds one predicate for all three (`_filters()` in `domains/tasks/router.py`).
 *    `taskQuery()` below is the single place it becomes wire parameters.
 * 3. **The version signal carries no numbers.** `GET /task-version` answers an opaque digest
 *    and a `304` when nothing moved; a count in that body would hand a zone-scoped member the
 *    project's unscoped total (§E property 1).
 */

import type { Representation } from "@/api/client";
import { api, unwrap, unwrapFile, unwrapWithETag } from "@/api/client";
import type { components } from "@/api/generated/openapi";

import type { MyTaskFilters, TaskFilters } from "./filters";

type Schemas = components["schemas"];

export type Task = Schemas["TaskResponse"];
export type TaskBulkEdit = Schemas["TaskBulkEditRequest"];
export type TaskBulkEditResult = Schemas["TaskBulkEditResponse"];
export type TaskCounts = Schemas['TaskCountsResponse'];
export type TaskPage = Schemas["Page_TaskResponse_"];
export type TaskKind = Schemas["TaskKind"];
export type TaskStatus = Schemas["TaskStatus"];
export type TaskPriority = Schemas["TaskPriority"];
export type TaskAnchor = Schemas["TaskAnchor"];
export type TaskVersion = Schemas["TaskVersionResponse"];
export type TaskCreate = Schemas["TaskCreateRequest"];
/** `KLQ-20260825-001` — bulk creation over the nodes the slicer and the filter leave. */
export type TaskGenerate = Schemas["TaskGenerateRequest"];
export type TaskGenerationPlan = Schemas["TaskGenerationPlanResponse"];
export type GeneratedTask = Schemas["TaskGeneratedTaskResponse"];
export type GeneratedSlotClaim = Schemas["TaskSlotClaimResponse"];
export type NodeFilters = Schemas["TaskNodeFilterRequest"];
export type TaskUpdate = Schemas["TaskUpdateRequest"];
export type TaskComment = Schemas["TaskCommentResponse"];
export type TaskAdmission = Schemas["TaskAdmissionResponse"];
export type TaskAttachment = Schemas["TaskAttachmentResponse"];
export type AttachmentKind = TaskAttachment["kind"];
export type TaskWatcherList = Schemas["TaskWatcherListResponse"];
export type TaskDeliverable = Schemas["TaskDeliverableResponse"];
export type DeliverableStatus = TaskDeliverable["status"];
export type TaskAudienceEntry = Schemas["TaskAudienceEntryResponse"];
export type AudienceReason = TaskAudienceEntry["reason"];
export type ProjectLabel = Schemas["ProjectLabelResponse"];
export type LabelColor = ProjectLabel["color"];
/** The studio's own vocabulary of what a task is (`KLQ-20260825-005`). */
export type WorkspaceTaskType = Schemas["WorkspaceTaskTypeResponse"];
export type TaskTypeCreate = Schemas["WorkspaceTaskTypeCreateRequest"];
export type TaskTypeUpdate = Schemas["WorkspaceTaskTypeUpdateRequest"];
/**
 * The glyph allowlist, off the wire rather than retyped.
 *
 * TWIN: `TASK_TYPE_ICONS` in `domains/tasks/models.py` and the migration's
 * `ck_workspace_task_type_icon`. This is the generated union, so `taskTypeIcons.tsx` — the
 * third copy, which decides what can be *drawn* — is checked against the backend's list by the
 * compiler rather than by somebody remembering.
 */
export type TaskTypeIcon = Schemas["TaskTypeIcon"];

/**
 * What an inline creation paints a new label.
 *
 * Neutral, and deliberately not a choice. Recolouring, renaming, reordering and archiving are
 * the project settings surface's (`KLQ-20260820-009`) — offering a palette in a filter bar
 * would be half of an administration screen this one does not own, and the half that has to
 * agree with the other half about the token names. A word is what the person came here to add.
 */
export const DEFAULT_LABEL_COLOR: LabelColor = "neutral-500";

/**
 * The three enums, as ordered tuples rather than as strings typed at each call site.
 *
 * TWIN: `TaskKind`, `TaskStatus` and `TaskPriority` in `domains/tasks/models.py`. The types
 * above come off the wire and would catch a *renamed* value at compile time; these carry the
 * **order** the UI renders in, which no type can. `TASK_STATUSES` is also the board's column
 * order (§C) — open, in progress, done, closed, left to right.
 */
export const TASK_KINDS: readonly TaskKind[] = ["task", "issue", "question"];
export const TASK_STATUSES: readonly TaskStatus[] = [
  "open",
  "in_progress",
  "done",
  "closed",
];
export const TASK_PRIORITIES: readonly TaskPriority[] = [
  "high",
  "normal",
  "low",
];

/**
 * The filter object as the wire sees it — the one mapping, read by the list and by the export.
 *
 * `under`, `label_ids` and the four any-of sets — `kind`, `status`, `priority` and
 * `assignee_user_id` — go out as **repeated** parameters, which is what the API's filter rules
 * mean by OR within one field; the URL carries them comma-joined because that is the form a
 * person can type (`./filters.ts`). The translation happens here and only here.
 *
 * TWIN: `_filters()` in `domains/tasks/router.py`. A field that changes arity changes in both,
 * in one commit (`KLQ-20260825-003` §F).
 *
 * Values are spread conditionally rather than passed as `undefined`: `exactOptionalPropertyTypes`
 * is on, and an explicit `undefined` serialises as an empty parameter the server then has to
 * decide about.
 */
export function taskQuery(filters: TaskFilters) {
  return {
    ...(filters.under.length === 0 ? {} : { under: [...filters.under] }),
    ...(filters.saved === null ? {} : { saved: filters.saved }),
    // **Sets, sent as repeated parameters** (`KLQ-20260825-003` §B). The URL carries them
    // comma-joined because that is the form a person can type; the repeated form is produced
    // here, and an empty set is an *absent* parameter rather than an empty one — `?status=`
    // would be a filter the server then has to decide about.
    ...(filters.kind.length === 0 ? {} : { kind: [...filters.kind] }),
    ...(filters.status.length === 0 ? {} : { status: [...filters.status] }),
    ...(filters.priority.length === 0
      ? {}
      : { priority: [...filters.priority] }),
    ...(filters.assignee.length === 0
      ? {}
      : { assignee_user_id: [...filters.assignee] }),
    // Its own flag beside the set, not a member of it: the server ORs the two into one
    // predicate (§C), so *"Ana's, plus what nobody owns"* is one question and one request.
    ...(filters.unassigned ? { unassigned: true } : {}),
    ...(filters.dueAfter === null ? {} : { due_after: filters.dueAfter }),
    ...(filters.dueBefore === null ? {} : { due_before: filters.dueBefore }),
    ...(filters.hasAttachments === null
      ? {}
      : { has_attachments: filters.hasAttachments }),
    ...(filters.drawingLinked === null
      ? {}
      : { drawing_linked: filters.drawingLinked }),
    ...(filters.labels.length === 0 ? {} : { label_ids: [...filters.labels] }),
    // The task's **deliverables'** objects, not the task's — see `TaskFilters.objects`
    // and `_produces_for_any` on the server, which is the same sentence as SQL.
    ...(filters.objects.length === 0 ? {} : { object_id: [...filters.objects] }),
    ...(filters.resolved === null ? {} : { resolved: filters.resolved }),
  };
}

/**
 * One page of the project's tasks, newest number first.
 *
 * A board column is this same call with `status` pinned in the filter object it is handed —
 * there is no second function for it, because it is not a second question (§C).
 */
export function listTasks(
  projectId: string,
  filters: TaskFilters,
  cursor: string | null,
): Promise<TaskPage> {
  return unwrap(
    api.GET("/api/v1/projects/{project_id}/tasks", {
      params: {
        path: { project_id: projectId },
        query: {
          ...taskQuery(filters),
          ...(cursor === null ? {} : { cursor }),
        },
      },
    }),
  );
}

/**
 * The tasks the caller is assigned to, in every project of every workspace
 * (`GET /api/v1/tasks`, `MCK-20260521-009`).
 *
 * **Not `taskQuery()`.** That mapping speaks a project's vocabulary — `under`, labels, saved
 * filters — and this endpoint refuses every parameter it does not declare with `422`. The five it
 * does declare are written out here, so a field added to the register's object cannot reach this
 * request by being spread in.
 */
export function listMyTasks(
  filters: MyTaskFilters,
  cursor: string | null,
): Promise<TaskPage> {
  return unwrap(
    api.GET("/api/v1/tasks", {
      params: {
        query: {
          ...(filters.kind.length === 0 ? {} : { kind: [...filters.kind] }),
          ...(filters.status.length === 0 ? {} : { status: [...filters.status] }),
          ...(filters.resolved === null ? {} : { resolved: filters.resolved }),
          ...(filters.dueAfter === null ? {} : { due_after: filters.dueAfter }),
          ...(filters.dueBefore === null ? {} : { due_before: filters.dueBefore }),
          // Whose work. `everyone` and a list of people are alternatives, never both — the
          // URL already holds only one of them, and sending both would ask the server a
          // question the screen is not showing.
          ...(filters.everyone
            ? { everyone: true }
            : filters.people.length === 0
              ? {}
              : { assignee_user_id: [...filters.people] }),
          ...(cursor === null ? {} : { cursor }),
        },
      },
    }),
  );
}

/**
 * One level's own tasks — what the plan asks for when somebody opens a branch.
 *
 * **`depth=1`, and the 1 is the whole point.** A task hangs *on* its level, so one level's own
 * work is `under=<node>&depth=1`. The drawings tree asks `depth=2` for the equivalent, because
 * a drawing is a child node of the level it sits on; same rule, one level apart. Two open
 * levels asked for this way cannot overlap, which is what makes this safe with no pruning
 * afterwards — the project root is open by default, and a subtree fetch of the root would be
 * the whole project.
 *
 * **`pages` walks the cursor, and the whole walk is one cache entry.**
 * `@tanstack/react-query`'s `useQueries` takes plain options, not infinite ones, and how many
 * branches are open is not knowable at compile time — so the chain lives here and the page
 * count is part of the key. Raising it re-walks from the first page, which is the cost of the
 * shape: one extra page's worth of rows per raise, on the rare branch that has more than
 * `BRANCH_PAGE` of them. The alternative is a component per branch so each can hold its own
 * `useInfiniteQuery`, which means the table rendering rows it does not own.
 *
 * `has_more` on the answer is the **last** page's, so a branch that ran out mid-walk stops
 * offering to continue.
 */
export async function listBranchTasks(
  projectId: string,
  filters: TaskFilters,
  nodeId: string,
  pages = 1,
): Promise<TaskPage> {
  const items: Task[] = [];
  let cursor: string | null = null;
  let hasMore = false;

  for (let page = 0; page < pages; page += 1) {
    const answer: TaskPage = await unwrap(
      api.GET("/api/v1/projects/{project_id}/tasks", {
        params: {
          path: { project_id: projectId },
          query: {
            ...taskQuery(filters),
            // The rail's own `under=` is replaced rather than joined: the branch *is* the
            // narrowing, and a level drawn inside the slice is already inside it.
            under: [nodeId],
            depth: 1,
            limit: BRANCH_PAGE,
            ...(cursor === null ? {} : { cursor }),
          },
        },
      }),
    );
    items.push(...answer.items);
    hasMore = answer.has_more;
    cursor = answer.next_cursor ?? null;
    if (!hasMore || cursor === null) break;
  }

  return { items, has_more: hasMore, next_cursor: cursor };
}

/**
 * How many of one level's tasks arrive per branch page.
 *
 * `MAX_LIMIT` on the server is 200 and it **clamps rather than refuses**, so asking for more
 * than this in one request buys nothing — which is why the branch walks the cursor instead of
 * raising the limit. Set to the cap so that a real floor is one request and the walk is the
 * exception.
 *
 * TWIN: `MAX_LIMIT` in `backend/src/kalqa/core/pagination.py`. Raising it there without raising
 * it here costs a round trip per 200 rows; lowering it there without lowering it here costs
 * nothing, because the server clamps.
 */
export const BRANCH_PAGE = 200;

/**
 * How much work hangs beneath each level, under the register's current question.
 *
 * **Not a second copy of the list, and not derived from it.** The register is paginated, so
 * counting the tasks that have arrived answers *"how many are loaded"* — which is the number
 * the plan used to print, and the reason a floor whose tasks sat on page three vanished from
 * the plan entirely. This asks the server the same question the list is asking and gets the
 * total.
 *
 * **It takes the same filters, and that is the point.** A count that ignored `status=open`
 * would put *"12"* above a branch that opens to show three — `KLQ-20260820-005`'s *"a count
 * that ignores the other active filters is a lie stated in numerals"*. `taskQuery` is the same
 * function the list uses, so the two cannot ask different things.
 */
export function fetchTaskCounts(
  projectId: string,
  filters: TaskFilters,
  under: readonly string[] = [],
): Promise<TaskCounts> {
  return unwrap(
    api.GET("/api/v1/projects/{project_id}/task-counts", {
      params: {
        path: { project_id: projectId },
        query: {
          ...taskQuery(filters),
          ...(under.length === 0 ? {} : { under: [...under] }),
        },
      },
    }),
  );
}

/**
 * Raise a task, an issue or a question — the register's own entrance, and the node-narrowed
 * register's, which is what pre-fills the location.
 *
 * The placement rules are the server's and are answered as `422`s this side renders rather
 * than re-implements (`domains/tasks/service.py:_place`): a pin anchor requires
 * `hierarchy_node_id` to be a **drawing** — a container has no sheet to pin to — and an anchor
 * with no node at all has nowhere to be pinned. The drawer offers the pin inputs only where
 * its cached tree says the node is a drawing, but the tree is a cache and the refusal is the
 * server's sentence, not a guess here.
 *
 * `source_version_id` is never sent from this entrance: it is *the version that was on
 * screen when the pin was placed*, and this drawer has no viewer on screen. The capture flow
 * that does is the pin layer's (`KLQ-20260820-001`).
 */
export function createTask(projectId: string, body: TaskCreate): Promise<Task> {
  return unwrap(
    api.POST("/api/v1/projects/{project_id}/tasks", {
      params: { path: { project_id: projectId } },
      body,
    }),
  );
}

/**
 * One intent over a selection — `KLQ-20260825-006` §E, and the write path the grid's cell
 * editing uses (`KLQ-20260828-001`).
 *
 * **No `If-Match`, deliberately.** Every collection field is a delta and every scalar is a
 * value the person chose for the whole selection, so there is no read-modify-write to lose.
 * `PATCH /tasks/{id}` keeps the validator, because that endpoint *is* a read-modify-write.
 *
 * The answer is a `200` naming what was applied and what was skipped, per row: a selection
 * legitimately holds closed tasks and tasks this caller may not edit, and refusing all forty
 * over one of them would make the feature unusable on exactly the selections it is for.
 */
export function bulkEditTasks(
  projectId: string,
  body: TaskBulkEdit,
): Promise<TaskBulkEditResult> {
  return unwrap(
    // `PATCH` on the collection since 2026-08-28 — see the route's own comment; the body is
    // unchanged, `task_ids` names the members.
    api.PATCH("/api/v1/projects/{project_id}/tasks", {
      params: { path: { project_id: projectId } },
      body,
    }),
  );
}

/**
 * Raise one task on every node the slicer and the filter leave standing (`KLQ-20260825-001`).
 *
 * **The nodes are not sent.** `under` is the slicer's parameter — the same array the register
 * and the board already put on the wire — and the server intersects it with the caller's zone
 * scope and with `filters`. There is no id list for this screen to compute, which is what
 * makes *"generate where I can see"* a property of the request rather than of this file's
 * arithmetic.
 *
 * **One endpoint for the preview and the write**, told apart by `dry_run` and answered by the
 * same body either way (`created` says which). A second preview endpoint could disagree with
 * the write about what it would do; this one cannot.
 */
export function generateTasks(
  projectId: string,
  body: TaskGenerate,
): Promise<TaskGenerationPlan> {
  return unwrap(
    api.POST("/api/v1/projects/{project_id}/task-generations", {
      params: { path: { project_id: projectId } },
      body,
    }),
  );
}

/**
 * *"Did anything move?"* — the 30 s poll of §E, and the whole of this screen's liveness story.
 *
 * The answer is an opaque digest. A caller holding the previous one learns only that it
 * changed, which is all the invalidation rule in `queries.ts` needs; an unchanged project
 * costs a header round-trip, because the endpoint raises before it builds a body.
 */
export function readTasksVersion(projectId: string): Promise<TaskVersion> {
  return unwrap(
    api.GET("/api/v1/projects/{project_id}/task-version", {
      params: { path: { project_id: projectId } },
    }),
  );
}

/**
 * One task, with the validator its `PATCH` demands back.
 *
 * `unwrapWithETag` and not `unwrap`, because `PATCH /tasks/{id}` refuses without an
 * `If-Match` (`428`, `MCK-20260812-006` §K) — a task is edited by several firms at once, and
 * the header is what stops a lost update silently overwriting what somebody else just agreed
 * on site. The representation and its validator travel together (`features/settings` made
 * this call first, for `PATCH /workspace`).
 */
export function readTask(taskId: string): Promise<Representation<Task>> {
  return unwrapWithETag(
    api.GET("/api/v1/tasks/{task_id}", {
      params: { path: { task_id: taskId } },
    }),
  );
}

/**
 * Edit the task's content — never its status, which has its own endpoint and its own rules.
 *
 * `etag` is the validator the cached representation carried; there is no overload without
 * one, deliberately. A stale one is `412` carrying the current representation, so the screen
 * can show what changed under the caller's feet rather than "something went wrong". A closed
 * task is `409` — the record stands, and the screen does not offer the edit in the first
 * place (`KLQ-20260821-001`, the closed-task criterion).
 */
export function updateTask(
  taskId: string,
  changes: TaskUpdate,
  etag: string,
): Promise<Representation<Task>> {
  return unwrapWithETag(
    api.PATCH("/api/v1/tasks/{task_id}", {
      params: { path: { task_id: taskId } },
      body: changes,
      headers: { "If-Match": etag },
    }),
  );
}

/**
 * A new task pointing at a closed one — `201`, because nothing about the closed row moves.
 *
 * `closed` is terminal: the record of what was decided is not edited, and saying something
 * new about the work means raising it again. The response is the new task, carrying
 * `reopened_from_task_id`; the old one is untouched, which is why the forward link is the
 * mutation result's to render (`TaskDetail`) rather than a field on the closed row.
 */
export function reopenTask(taskId: string): Promise<Task> {
  return unwrap(
    api.POST("/api/v1/tasks/{task_id}/reopen", {
      params: { path: { task_id: taskId } },
      body: {},
    }),
  );
}

/**
 * The thread, oldest first, as the plain list the server answers — not a `Page`.
 *
 * Unpaginated by the server's own decision (spec Notes: bounded in practice on one task), so
 * this screen consumes it as it is and does not paginate what the server does not. The list
 * is already cut at the caller's admission server-side; nothing here filters it further.
 */
export function listTaskComments(
  taskId: string,
): Promise<Schemas["TaskCommentListResponse"]> {
  return unwrap(
    api.GET("/api/v1/tasks/{task_id}/comments", {
      params: { path: { task_id: taskId } },
    }),
  );
}

/**
 * Say something on the task.
 *
 * No `mentions_user_ids`: naming somebody at another firm admits that firm, and the picker
 * that states that admission before the act is not built here — a mention UI without the
 * forced conversation would perform admissions silently, which is the one thing the audience
 * design forbids. Until that picker exists, a comment from this screen admits nobody.
 */
export function addTaskComment(
  taskId: string,
  body: string,
  mentionsUserIds: readonly string[],
): Promise<TaskComment> {
  return unwrap(
    api.POST("/api/v1/tasks/{task_id}/comments", {
      params: { path: { task_id: taskId } },
      body: { body, mentions_user_ids: [...mentionsUserIds] },
    }),
  );
}

/** The task's attachments, cut at the caller's admission exactly as the thread is. */
export function listTaskAttachments(
  taskId: string,
): Promise<Schemas["TaskAttachmentListResponse"]> {
  return unwrap(
    api.GET("/api/v1/tasks/{task_id}/attachments", {
      params: { path: { task_id: taskId } },
    }),
  );
}

/**
 * Turn a completed upload into an attachment on this task — the consuming half of the
 * `POST /uploads` ticket flow. The bytes never pass through the API (ADR-0005): the upload
 * id names an object already stored and validated, and the server consumes it exactly once.
 */
export function addTaskAttachment(
  taskId: string,
  uploadId: string,
  kind: AttachmentKind,
): Promise<TaskAttachment> {
  return unwrap(
    api.POST("/api/v1/tasks/{task_id}/attachments", {
      params: { path: { task_id: taskId } },
      body: { upload_id: uploadId, kind },
    }),
  );
}

/**
 * The drawings this task owns (`KLQ-20260824-001`) — the C8 Deliverables section's list.
 *
 * A sub-resource like the thread and the watchers, not a field on the task: the register's
 * pages must not carry (or falsely imply an empty) per-task list. Already cut to the caller's
 * zone scope on the way here — a drawing outside their zones is absent, not greyed out,
 * exactly as it is absent from the tree. A closed task still answers: ownership is a record.
 */
export function listTaskDeliverables(
  taskId: string,
): Promise<Schemas["TaskDeliverableListResponse"]> {
  return unwrap(
    api.GET("/api/v1/tasks/{task_id}/deliverables", {
      params: { path: { task_id: taskId } },
    }),
  );
}

/** Who follows this task, and whether the caller is one of them. */
export function listTaskWatchers(taskId: string): Promise<TaskWatcherList> {
  return unwrap(
    api.GET("/api/v1/tasks/{task_id}/watchers", {
      params: { path: { task_id: taskId } },
    }),
  );
}

/**
 * Follow the task, or stop. `PUT`, because the caller states the state they want rather than
 * the change they intend — following twice is following. Only ever about the caller.
 */
export function setTaskWatching(
  taskId: string,
  watching: boolean,
): Promise<TaskWatcherList> {
  return unwrap(
    api.PUT("/api/v1/tasks/{task_id}/watchers", {
      params: { path: { task_id: taskId } },
      body: { watching },
    }),
  );
}

/**
 * Which firms were let into this task, why, and from when (§F).
 *
 * A display of a fact, not a control: the visibility of the admission — the firm, the reason
 * and the date — is the agreed mitigation for admission happening as a side effect of
 * ordinary work. An external caller reads their own firm's row and nothing else; the cut is
 * the server's.
 */
export function readTaskAudience(
  taskId: string,
): Promise<Schemas["TaskAudienceResponse"]> {
  return unwrap(
    api.GET("/api/v1/tasks/{task_id}/audience", {
      params: { path: { task_id: taskId } },
    }),
  );
}

/**
 * One move through the workflow — what a dropped card asks for, and all of it (§C).
 *
 * The body carries the target status and nothing else. `resolved_by_version_id` is the task
 * detail's to send when a close comes out of a version; a drag has no version in hand and
 * inventing one here would be exactly the optimistic write §C forbids. Which targets may be
 * asked for at all is the row's `allowed_transitions` — the graph lives in
 * `domains/tasks/service.py:_TRANSITIONS` and is re-implemented nowhere, so the two `409`s
 * (an illegal jump, and the verification gate naming who has to close) arrive as the server's
 * own sentences and are rendered as such.
 */
export function changeTaskStatus(
  taskId: string,
  status: TaskStatus,
): Promise<Task> {
  return unwrap(
    api.POST("/api/v1/tasks/{task_id}/status", {
      params: { path: { task_id: taskId } },
      body: { status },
    }),
  );
}

/**
 * Close one task **as resolved by a version** — the publish flow's close, and only its
 * (`MCK-20260521-008`, *"the task's output is a drawing"*).
 *
 * The one caller that sends `resolved_by_version_id`, because it is the one place a person has
 * a published version in hand and has ticked the task it resolved. The server refuses a
 * version from another drawing, and `closed` is reachable only from `done` — the caller walks
 * the task there first (`queries.ts::useCloseTasksByVersion`).
 */
export function closeTaskByVersion(taskId: string, versionId: string): Promise<Task> {
  return unwrap(
    api.POST("/api/v1/tasks/{task_id}/status", {
      params: { path: { task_id: taskId } },
      body: { status: "closed", resolved_by_version_id: versionId },
    }),
  );
}

/**
 * What a drawing still owes: its `open`, `in_progress` and `done` tasks, newest first
 * (`GET /projects/{id}/slot-tasks`). The publish flow proposes from this list and the slot
 * detail shows it; nothing about reading it closes anything.
 */
export function listSlotTasks(projectId: string, slotId: string): Promise<Task[]> {
  return unwrap(
    api.GET("/api/v1/projects/{project_id}/slot-tasks", {
      params: { path: { project_id: projectId }, query: { slot_id: slotId } },
    }),
  ).then((response) => response.items);
}

/**
 * The filter currently on screen, as a CSV file.
 *
 * The same `taskQuery()` the list sends, which is what makes *"export the current filter"*
 * structurally true rather than maintained: two call sites, one mapping, and the server
 * answers both from one predicate. Every boundary the list applies applies here — zone scope
 * and the audience — so an export can never carry a row the same caller could not see.
 */
export function exportTasksCsv(
  projectId: string,
  filters: TaskFilters,
): Promise<Blob> {
  return unwrapFile(
    api.GET("/api/v1/projects/{project_id}/task-exports", {
      params: { path: { project_id: projectId }, query: taskQuery(filters) },
      parseAs: "blob",
    }),
  );
}

/**
 * The project's label vocabulary, in picker order.
 *
 * Unpaginated by design on the server: a vocabulary is tens of rows and is read by every task
 * form and every filter bar. Reference data, so `queries.ts` fetches it once per project and
 * shares it rather than looking a label up per row (`~/.claude/CLAUDE.md`, data fetching).
 */
export function listLabels(
  projectId: string,
): Promise<Schemas["ProjectLabelListResponse"]> {
  return unwrap(
    api.GET("/api/v1/projects/{project_id}/labels", {
      params: { path: { project_id: projectId } },
    }),
  );
}

/**
 * One word added to the vocabulary, from wherever somebody needed it.
 *
 * The collision is the server's answer, not a check here: `POST` refuses a word the project
 * already has — case-insensitively — with a `409` whose sentence names the existing label. A
 * client-side scan of the fetched registry would agree with it only until somebody on another
 * screen adds the same word, and would disagree about case in whichever direction it guessed.
 */
export function createLabel(
  projectId: string,
  name: string,
): Promise<ProjectLabel> {
  return unwrap(
    api.POST("/api/v1/projects/{project_id}/labels", {
      params: { path: { project_id: projectId } },
      body: { name, color: DEFAULT_LABEL_COLOR },
    }),
  );
}

/**
 * The studio's task types — what a task *is*, as a vocabulary rather than as prose
 * (`KLQ-20260825-005`).
 *
 * **Workspace-scoped, and there is no project in any of these paths.** The registry belongs to
 * the firm: it is read by the generate drawer on every project, edited on one settings screen,
 * and rendered by every task row that names a type. `queries.ts` fetches it once per session
 * and shares it — a lookup per row would be the shape the label registry already refuses.
 *
 * **Archived rows come back and the client filters them.** The settings screen needs them
 * (they are what *unarchive* acts on) and the register needs them (a task keeps wearing the
 * type it names), so the one surface that must not offer them — the picker — is the one place
 * that filters.
 */
export function listTaskTypes(): Promise<
  Schemas["WorkspaceTaskTypeListResponse"]
> {
  return unwrap(api.GET("/api/v1/workspace/task-types"));
}

export function createTaskType(
  body: TaskTypeCreate,
): Promise<WorkspaceTaskType> {
  return unwrap(api.POST("/api/v1/workspace/task-types", { body }));
}

export function updateTaskType(
  taskTypeId: string,
  body: TaskTypeUpdate,
): Promise<WorkspaceTaskType> {
  return unwrap(
    api.PATCH("/api/v1/workspace/task-types/{task_type_id}", {
      params: { path: { task_type_id: taskTypeId } },
      body,
    }),
  );
}

/**
 * Retire a type. **Which of the two outcomes happens is the server's decision**, taken from
 * whether any task names it: a type nothing has used is deleted, one a task names is archived.
 * `deletable` on the row is how the screen says which before the person clicks, and the list
 * is re-read afterwards rather than guessed at.
 */
export function deleteTaskType(taskTypeId: string): Promise<unknown> {
  return unwrap(
    api.DELETE("/api/v1/workspace/task-types/{task_type_id}", {
      params: { path: { task_type_id: taskTypeId } },
    }),
  );
}
